import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { BasicFilters, PaginationResponse } from 'src/app/core/services/async-list.service';

interface Page {
  selected: boolean;
  value: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sky-pagination[pagination]',
  templateUrl: './pagination.component.html',
  styles: [
    `
      :host {
        display: flex;
        align-items: center;

        @apply mobile:flex-col;
      }

      .page {
        @apply font-semibold w-full px-3 py-1;
      }

      .page.selected {
        @apply rounded-full bg-sky-grey-light;
      }
    `,
  ],
})
export class PaginationComponent<F extends BasicFilters> implements OnInit, OnChanges {
  @Input() public filters$!: BehaviorSubject<F>;
  @Input() public pagination!: PaginationResponse;

  public isMobileView$ = this._appService.isMobileView$;
  public sizeControl!: FormControl;
  public isFirstThreePage!: boolean;
  public isLastThreePage!: boolean;

  private readonly _pages$ = new BehaviorSubject<Array<Page>>([]);

  constructor(private readonly _appService: AppService) {}

  public get pages$(): Observable<Array<Page>> {
    return this._pages$.asObservable();
  }

  public ngOnInit() {
    this.calculatePaginationProps();

    this.sizeControl.valueChanges.subscribe(value => {
      this.filters$.next({
        ...this.filters$.getValue(),
        size: value,
      });
    });
  }

  public ngOnChanges({ totalElements, pagination, filters$ }: SimpleChanges): void {
    if (filters$) {
      this.sizeControl = new FormControl(filters$.currentValue.getValue().size);
    }

    if (totalElements?.firstChange || pagination?.firstChange) {
      return;
    }

    this.calculatePaginationProps();
  }

  public isFirstPageSelected(pages: Array<Page>): boolean {
    return pages[0].selected;
  }

  public isLastPageSelected(pages: Array<Page>): boolean {
    return pages[pages.length - 1].selected;
  }

  public nextPage(pages: Array<Page>): void {
    const indexOfSelectedPage = pages.findIndex(page => page.selected);

    this.selectPage(pages[indexOfSelectedPage + 1].value);
  }

  public previousPage(pages: Array<Page>): void {
    const indexOfSelectedPage = pages.findIndex(page => page.selected);

    this.selectPage(pages[indexOfSelectedPage - 1].value);
  }

  public selectPage(pageValue: number): void {
    this.isFirstThreePage = pageValue <= 2;
    this.isLastThreePage = pageValue >= this.pagination.totalPages - 1 - 2;

    this.filters$.next({
      ...this.filters$.getValue(),
      page: pageValue,
    });
  }

  private getPages(currentPage: number, totalPages: number): Array<Page> {
    const arePagesHidden = totalPages > 6;

    if (!arePagesHidden) {
      return Array.from({ length: totalPages }, (_, index) => ({
        selected: currentPage === index,
        value: index,
      }));
    }

    let baseValue: number;

    if (currentPage === 0) {
      baseValue = 0;
    } else if (currentPage === totalPages - 1) {
      baseValue = totalPages - 1 - 2;
    } else {
      baseValue = currentPage - 1;
    }

    return Array.from({ length: 3 }, (_, index) => ({
      selected: currentPage === baseValue + index,
      value: baseValue + index,
    }));
  }

  private calculatePaginationProps() {
    const pageValue = this.pagination.number;
    const totalPages = this.pagination.totalPages;

    this.isFirstThreePage = pageValue <= 2;
    this.isLastThreePage = pageValue >= totalPages - 1 - 2;

    this._pages$.next(this.getPages(pageValue, totalPages));
  }
}
